import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Building2, Loader2 } from "lucide-react";
import { guestHotelServicesApi } from "@/api/guest/guestHotelServicesApi";
import { useSession } from "@/hooks/useSession.js";
import { toast } from "sonner";

const PAPER_WIDTH = 794;

const getApiData = (response) => {
  return response?.data?.data || response?.data?.Data || response?.data || {};
};

const getApiMessage = (error, fallback = "Something went wrong.") => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.response?.data?.title ||
    fallback
  );
};

const getPagedItems = (response) => {
  const data = getApiData(response);

  return (
    data.items ||
    data.Items ||
    data.data ||
    data.Data ||
    (Array.isArray(data) ? data : [])
  );
};


const normalizeService = (service) => {
  return {
    id: service.id || service.Id,
    serviceCategoryId: service.serviceCategoryId || service.ServiceCategoryId,
    serviceCategoryName:
      service.serviceCategoryName ||
      service.ServiceCategoryName ||
      "Other Services",
    name: service.name || service.Name || "Untitled Service",
    code: service.code || service.Code || "",
    description: service.description || service.Description || "",
    price: service.price ?? service.Price,
    isPriceFixed: service.isPriceFixed ?? service.IsPriceFixed ?? false,
    unitLabel: service.unitLabel || service.UnitLabel || "",
    imageUrl: service.imageUrl || service.ImageUrl || "",
    estimatedDurationMinutes:
      service.estimatedDurationMinutes || service.EstimatedDurationMinutes,
    displayOrder: service.displayOrder ?? service.DisplayOrder ?? 0,
    isAvailable: service.isAvailable ?? service.IsAvailable ?? true,
    createdAtUtc: service.createdAtUtc || service.CreatedAtUtc,
    updatedAtUtc: service.updatedAtUtc || service.UpdatedAtUtc,
  };
};

const buildServiceDocument = (services, hotel) => {
  const grouped = services.reduce((acc, service) => {
    const categoryName = service.serviceCategoryName || "Other Services";

    if (!acc[categoryName]) {
      acc[categoryName] = {
        id: service.serviceCategoryId || categoryName,
        title: categoryName,
        rows: [],
      };
    }

    acc[categoryName].rows.push({
      id: service.id,
      name: service.name,
      description:
        service.description?.trim() || "Service details are not available.",
      displayOrder: service.displayOrder,
    });

    return acc;
  }, {});

  const sections = Object.values(grouped).map((section) => ({
    ...section,
    rows: section.rows.sort(
      (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
    ),
  }));

  return {
    greetingTitle: "Dear Guest,",
    greetingSubtitle: `Welcome to ${hotel.name}`,
    welcomeMessage:
      "Please find below the list of hotel services available during your stay.",
    sections,
    assistanceNote:
      "For assistance with any of the listed services, please contact the front desk or hotel support team.",
    signOff: {
      message: "We wish you a pleasant and comfortable stay.",
      name: "Hotel Management",
      title: "Guest Services",
    },
  };
};

const GuestHotelServicesPage = () => {
  const { hotel: sessionHotel } = useSession();
  const hotel = {
    name: sessionHotel?.name || sessionHotel?.Name || "Hotel",
    location: sessionHotel?.location || sessionHotel?.Location || "",
    phone: sessionHotel?.phone || sessionHotel?.Phone || "",
    email: sessionHotel?.email || sessionHotel?.Email || "",
    address: sessionHotel?.address || sessionHotel?.Address || "",
  };

  const paperRef = useRef(null);

  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paperHeight, setPaperHeight] = useState(0);
  const [initialScale, setInitialScale] = useState(1);

  const loadServiceDocument = async () => {
    try {
      setLoading(true);

      const response = await guestHotelServicesApi.getAll();

      const services = getPagedItems(response).map(normalizeService);
      const serviceDocument = buildServiceDocument(services, hotel);

      setDocumentData(serviceDocument);
    } catch (error) {
      toast.error(getApiMessage(error, "Failed to load hotel services."));
      setDocumentData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServiceDocument();
  }, []);

  useEffect(() => {
    const updateScale = () => {
      const screenWidth = window.innerWidth;
      const padding = 24;
      const availableWidth = screenWidth - padding;

      if (availableWidth < PAPER_WIDTH) {
        setInitialScale(availableWidth / PAPER_WIDTH);
      } else {
        setInitialScale(1);
      }
    };

    updateScale();

    window.addEventListener("resize", updateScale);

    return () => window.removeEventListener("resize", updateScale);
  }, []);

  useEffect(() => {
    if (!paperRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setPaperHeight(entry.contentRect.height);
    });

    observer.observe(paperRef.current);

    return () => observer.disconnect();
  }, [documentData]);

  return (
    <>
      <Helmet>
        <title>{`Hotel Services - ${hotel.name}`}</title>
        <meta
          name="description"
          content={`Guest services and hotel information for ${hotel.name}`}
        />
      </Helmet>

      <div className="min-h-screen bg-slate-200">
        {loading ? (
          <div className="flex min-h-screen items-center justify-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
        ) : !documentData ? (
          <div className="mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4">
            <div className="w-full rounded-3xl border bg-white p-8 text-center shadow-sm">
              <Building2 className="mx-auto h-12 w-12 text-muted-foreground" />
              <h1 className="mt-4 text-2xl font-bold">
                No service information found
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Hotel service information is not available right now.
              </p>
            </div>
          </div>
        ) : (
          <div className="h-screen overflow-auto p-3 sm:p-6">
            <div
              className="mx-auto"
              style={{
                width: PAPER_WIDTH * initialScale,
                height: paperHeight ? paperHeight * initialScale : "auto",
              }}
            >
              <div
                ref={paperRef}
                style={{
                  width: PAPER_WIDTH,
                  transform: `scale(${initialScale})`,
                  transformOrigin: "top left",
                }}
                className="bg-white p-10 text-slate-900 shadow-2xl"
              >
                <div className="border border-amber-900/80 p-8">
                  <div className="mb-8 text-center">
                    <div className="mb-2 flex justify-center">
                      <Building2 className="h-11 w-11 text-amber-900" />
                    </div>

                    <h1 className="text-3xl font-bold uppercase tracking-wide text-amber-950">
                      {hotel.name}
                    </h1>

                    <p className="mt-1 text-sm font-medium uppercase tracking-[0.25em] text-amber-900">
                      {hotel.location}
                    </p>
                  </div>

                  <div className="mb-8 text-center text-sm">
                    <p className="font-semibold uppercase">
                      {documentData.greetingTitle}
                    </p>

                    <p className="mt-3 font-semibold uppercase">
                      {documentData.greetingSubtitle}
                    </p>

                    <p className="mx-auto mt-5 max-w-3xl font-medium uppercase leading-relaxed">
                      {documentData.welcomeMessage}
                    </p>
                  </div>

                  <div className="space-y-8">
                    {documentData.sections?.length === 0 ? (
                      <div className="border border-amber-900/70 p-5 text-center text-sm text-slate-500">
                        No service sections available.
                      </div>
                    ) : (
                      documentData.sections.map((section) => (
                        <section key={section.id}>
                          <h2 className="mb-3 text-center text-xl font-bold uppercase tracking-wide text-amber-900">
                            {section.title}
                          </h2>

                          <div className="overflow-hidden border border-amber-900/70">
                            {section.rows.length === 0 ? (
                              <div className="p-5 text-center text-sm text-slate-500">
                                No service details available.
                              </div>
                            ) : (
                              <table className="w-full border-collapse text-sm">
                                <tbody>
                                  {section.rows.map((row) => (
                                    <tr
                                      key={row.id}
                                      className="border-b border-amber-900/60 last:border-b-0"
                                    >
                                      <td className="w-52 border-r border-amber-900/60 px-3 py-2 align-top font-semibold">
                                        {row.name}
                                      </td>

                                      <td className="px-3 py-2 align-top leading-relaxed">
                                        <div className="flex gap-3">
                                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-700" />
                                          <p>{row.description}</p>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </section>
                      ))
                    )}
                  </div>

                  <div className="mt-8 text-sm leading-relaxed">
                    <p>{documentData.assistanceNote}</p>

                    <div className="mt-6">
                      <p>{documentData.signOff.message}</p>
                      <p>Sincerely,</p>

                      <div className="mt-5">
                        <p className="font-semibold">
                          {documentData.signOff.name}
                        </p>
                        <p>{documentData.signOff.title}</p>
                        <p>{hotel.name}</p>
                        <p>{hotel.phone}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-10 border-t border-amber-900/50 pt-5 text-center text-sm">
                    <p>{hotel.address}</p>
                    <p>
                      Tel - {hotel.phone}; Email - {hotel.email}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GuestHotelServicesPage;
